import type { BillingSummary } from '@/lib/billing';
import { UsageMeter } from './UsageMeter';
import { useBillingSummary } from './queries';
import {
  USAGE_TIER_LABEL,
  USAGE_TIER_ORG_LABEL,
  findUsage,
  workspaceDailyUsageItem,
} from './usage-nudge';

type Props = {
  usage: BillingSummary['usage'];
  warmupDaily?: BillingSummary['warmupDaily'];
};

/**
 * The sidebar workspace card's daily-sending meter: a bar, then
 * "96 / 150 emails · today". Shows the current workspace's daily budget when it
 * has one, else the org-wide Daily Sending Limit (captioned "org-wide"). The
 * tier label stays org-level (it's the plan-upgrade nudge) and appears once
 * usage crosses 70%; over a workspace meter it says "org-wide" so it can't be
 * read as describing that workspace's own figure.
 */
export function PlanCardUsage({ usage, warmupDaily }: Props) {
  const daily = findUsage(usage, 'DAILY_EMAILS');
  const ws = warmupDaily?.workspace;
  const wsLimit = ws?.limit ?? null;
  if (!daily && wsLimit === null) return null;

  const tierLabel = daily?.tier ? USAGE_TIER_LABEL[daily.tier] : null;

  if (ws && wsLimit !== null) {
    const item = workspaceDailyUsageItem(ws);
    const orgTierLabel = daily?.tier ? USAGE_TIER_ORG_LABEL[daily.tier] : null;
    return (
      <div className="flex flex-col gap-[7px]">
        {/* An unknown sent count gets no bar — never drawn as 0. */}
        {item ? <UsageMeter item={item} className="h-[5px]" /> : null}
        <div className="sidebar-quota-row num">
          <span>{`${ws.used ?? '-'} / ${wsLimit} emails`}</span>
          <span>today</span>
        </div>
        {orgTierLabel && <div className="sidebar-quota-tier">{orgTierLabel}</div>}
      </div>
    );
  }

  if (!daily) return null;
  return (
    <div className="flex flex-col gap-[7px]">
      <UsageMeter item={daily} className="h-[5px]" />
      <div className="sidebar-quota-row num">
        <span>
          {daily.limit === null
            ? `${daily.used} emails · Unlimited`
            : `${daily.used} / ${daily.limit} emails`}
        </span>
        <span>today, org-wide</span>
      </div>
      {tierLabel && <div className="sidebar-quota-tier">{tierLabel}</div>}
    </div>
  );
}

/**
 * The collapsed rail's meter under the workspace tile — the same figure as
 * `PlanCardUsage`, bar only, with the numbers in the tooltip. Renders nothing
 * while loading, on error, when unlimited, or when the sent count is unknown.
 */
export function PlanCardRailMeter() {
  const { data } = useBillingSummary();
  if (!data) return null;
  // Same precedence as `PlanCardUsage`: a workspace budget wins even when its
  // sent count is unknown (then no bar) — never silently the org total.
  const ws = data.warmupDaily?.workspace ?? null;
  const item =
    ws?.limit != null ? workspaceDailyUsageItem(ws) : findUsage(data.usage, 'DAILY_EMAILS');
  if (!item || item.limit === null) return null;
  return (
    <UsageMeter
      item={item}
      className="mt-1.5 h-[5px]"
      title={`${item.used} of ${item.limit} emails sent today`}
    />
  );
}
